/**
 * The decision strip under the case header: what is left to decide, in one row.
 *
 * From left to right:
 *   - the customer: "Confirm <name, city>" (with the second candidate and "Search") while
 *     it is a suggestion; the name with a check once it is certain or confirmed;
 *     "Customer not identified" with "Search" when there is none;
 *   - the lines: "3 of 4 lines matched, 1 decided", with "Accept N matched items";
 *   - the next open step as one sentence, with its action where one exists (Take,
 *     Pick line 2, Accept, Confirm, Prepare reply);
 *   - "Reply draft ready" (or "Prepared by I2Q on arrival"), else "Prepare reply"; a
 *     reply the server marks stale (made before the case changed, review F06) is not
 *     ready: it reads "Made before your changes" with "Prepare again", and a failed
 *     preparation says why here too (review F07);
 *   - "Lead time not known for N lines": one flag here instead of one per line;
 *   - why the case is back for you, when it is.
 *
 * After a customer confirmation it says that the lines are being matched again and offers
 * "Next case". Every write reaches the app database only; nothing is sent.
 */

import { ArrowRight, Check, Icon, ListChecks, Search, Sparkles } from "@norican/icons";
import { Badge, Button, Card, cn, Typography, useToastManager } from "@norican/react";
import { useState } from "react";

import {
  caseAccountsOf,
  customerSearchStart,
  PickCustomerDialog,
  placeOf,
} from "@/features/case-review/customer-card";
import { effectiveItem } from "@/features/case-review/line-decision";
import { lineStatusFor } from "@/features/case-review/line-semantics";
import { useShortcutHandler } from "@/hooks/use-shortcuts";
import {
  answerDraftFailureText,
  type CustomerCandidate,
  isDraftStale,
  personName,
  type SuggestedCustomer,
  useAcceptMatched,
  useAnswerDraft,
  useCaseAssign,
  useCaseCustomer,
  useCreateAnswerDraft,
  useFoDraft,
  workFailureText,
} from "@/lib/api";
import { caseStatusOf, heldStatusOf, REASON_CUSTOMER } from "@/lib/case-status";
import { FEATURES } from "@/lib/features";
import { logger } from "@/lib/logger";
import { type QuoteCaseDetail, type QuoteLine } from "@/lib/mock";
import {
  acceptedText,
  acceptMatchedLabel,
  backForYouReasons,
  confirmsInOneClick,
  customerConfirmedText,
  DRAFT_ON_REQUEST_TEXT,
  leadTimeUnknownText,
  MADE_BEFORE_CHANGES,
  PREPARED_BY,
  PREPARED_ON_ARRIVAL,
  salesResponsibleNote,
  suggestedCustomerText,
  usualOwnerText,
  usualOwnerTip,
} from "@/lib/sales-flow";
import { customerConfirmedLabel } from "@/lib/work-labels";

/** What the page counted on the lines (the page owns the line status rules). */
export interface StripLines {
  /** Lines to review (fallback lines left out). */
  total: number;
  matched: number;
  decided: number;
  /** Matched lines nobody decided yet: what "Accept matched items" would confirm. */
  acceptable: number;
  /** The first line a person has to pick, as "Line 2" and its id; null when none. */
  firstOpen: { label: string; id: string } | null;
  /** Lines with an item whose lead time is not known; null when no line has a price. */
  leadTimeUnknown: number | null;
  /** True while the customer is not certain: found items are not "matched" yet. */
  customerOpen?: boolean;
  /** Lines with an item, matched or suggested. */
  itemsFound?: number;
}

// The line status rule is shared with the Outlook pane (review F05).
export { lineStatusFor };

/** What the decision strip needs to know about the lines (fallback lines left out). */
export function stripLinesOf(realLines: readonly QuoteLine[], detail: QuoteCaseDetail): StripLines {
  realLines = realLines.filter((line) => line.requestAction !== "remove");
  const firstOpenIndex = realLines.findIndex(
    (line) =>
      !line.decision && (lineStatusFor(line) === "open" || lineStatusFor(line) === "suggested"),
  );
  const firstOpen = realLines[firstOpenIndex];
  const priced = realLines.some((line) => line.price != null);
  return {
    total: realLines.length,
    matched: realLines.filter((line) => lineStatusFor(line) === "matched").length,
    decided: realLines.filter((line) => line.decision).length,
    acceptable:
      (detail.workStatus ?? "open") === "open"
        ? realLines.filter(
            (line) =>
              lineStatusFor(line) === "matched" &&
              !line.decision &&
              Boolean(line.lineKey) &&
              Boolean(line.matchedItemId) &&
              line.enquiryText.trim() !== "",
          ).length
        : 0,
    firstOpen: firstOpen ? { label: `Line ${firstOpenIndex + 1}`, id: firstOpen.id } : null,
    customerOpen: detail.customerCertainty !== "certain",
    itemsFound: realLines.filter(
      (line) => line.matchedItemId != null || lineStatusFor(line) === "suggested" || line.decision,
    ).length,
    leadTimeUnknown: priced
      ? realLines.filter(
          (line) =>
            line.decision?.kind !== "no_item" &&
            effectiveItem(line).item != null &&
            line.price?.leadDays == null,
        ).length
      : null,
  };
}

/** The stored rule name of an agent's end customer without an account (`customer_resolution.how`). */
const HOW_END_CUSTOMER_OPEN = "agent-end-customer-open";
/**
 * Candidate kinds that are the SENDER'S past: on an agent's mail they are earlier end
 * customers of the agent, listed under "Why", never offered as "Confirm". The same rule as
 * `store.records.suggested_customers` on the server (sales review of 12 Oct 2026, C-02).
 */
const SENDER_PAST_KINDS: ReadonlySet<string> = new Set([
  "history",
  "contact_memory",
  "domain_memory",
]);

/** The customer of the case as the strip shows it. */
export type StripCustomer =
  | { state: "certain"; name: string }
  | { state: "suggested"; options: SuggestedCustomer[] }
  | { state: "none" };

/** Where the case stands on its customer, with at most two suggestions to confirm. */
export function stripCustomerOf(detail: QuoteCaseDetail): StripCustomer {
  const block = detail.customerBlock ?? null;
  const confirmedAccount = detail.work?.customer_account ?? null;
  if (detail.customerCertainty === "certain") {
    const name =
      (confirmedAccount && confirmedAccount !== block?.account
        ? (block?.candidates ?? []).find((c) => c.account === confirmedAccount)?.name
        : (block?.name ?? block?.company)) ||
      detail.customer ||
      (confirmedAccount ? `Account ${confirmedAccount}` : "");
    return { state: "certain", name };
  }
  // The engine's own word for "nobody proposed" counts whatever the status label says: on
  // an agent's mail whose end customer has no account, the listed accounts are the agent's
  // earlier end customers, never a suggestion (sales review of 12 Oct 2026, C-02).
  const notIdentified =
    detail.customerCertainty === "none" ||
    caseStatusOf(detail).label === REASON_CUSTOMER ||
    block?.how === HOW_END_CUSTOMER_OPEN;
  const fromRow = (detail.suggestedCustomers ?? []).filter((entry) => entry.account);
  const fromBlock: SuggestedCustomer[] = [];
  if (block?.account) {
    const own = (block.candidates ?? []).find((c) => c.account === block.account);
    fromBlock.push({
      account: block.account,
      name: block.name ?? block.company ?? null,
      city: own?.city ?? null,
    });
  }
  // When the server sends no suggestion the candidates stand in. On an agent's mail an
  // account of the agent's past never does, whatever reason the case is filed under (a
  // row filed before the re-derive, a reply in a thread): "Find customer" instead.
  const agentMail = block?.role === "agent";
  for (const candidate of block?.candidates ?? []) {
    if (agentMail && SENDER_PAST_KINDS.has(candidate.kind ?? "")) {
      continue;
    }
    if (!fromBlock.some((entry) => entry.account === candidate.account)) {
      fromBlock.push(toSuggested(candidate));
    }
  }
  const options = (fromRow.length > 0 ? fromRow : fromBlock).slice(0, 2);
  if (notIdentified || options.length === 0) {
    return { state: "none" };
  }
  return { state: "suggested", options };
}

function toSuggested(candidate: CustomerCandidate): SuggestedCustomer {
  return { account: candidate.account, name: candidate.name, city: placeOf(candidate) };
}

/** "3 of 4 lines matched, 1 decided". */
export function stripLinesText(lines: StripLines): string {
  if (lines.total === 0) {
    return "No lines found in the mail";
  }
  const decided = lines.decided > 0 ? `, ${lines.decided} decided` : "";
  if (lines.customerOpen && (lines.itemsFound ?? 0) > 0) {
    // The items wait for the customer: they are found, not matched yet.
    return `${Math.min(lines.itemsFound ?? 0, lines.total)} of ${lines.total} items found, customer to confirm${decided}`;
  }
  return `${lines.matched} of ${lines.total} ${lines.total === 1 ? "line" : "lines"} matched${decided}`;
}

/** The next open step, as one sentence, and which action belongs to it. */
export type NextStep =
  | { kind: "reading"; text: string }
  | { kind: "customer"; text: string }
  | { kind: "accept"; text: string }
  | { kind: "pick"; text: string; lineId: string }
  | { kind: "check"; text: string }
  | { kind: "take"; text: string }
  | { kind: "reply"; text: string }
  | { kind: "copy"; text: string }
  | { kind: "none"; text: string };

/** The first thing still open on the case, in the order sales work through it. */
export function nextStepOf(
  detail: QuoteCaseDetail,
  lines: StripLines,
  customer: StripCustomer,
  facts: { replyReady: boolean; firstBlockedCheck?: string | null },
): NextStep {
  if (detail.state === "queued" || detail.state === "processing") {
    return { kind: "reading", text: "I2Q is still reading this mail." };
  }
  const open = (detail.workStatus ?? "open") === "open";
  if (!open) {
    return { kind: "none", text: "Nothing is open on this case." };
  }
  if (customer.state !== "certain") {
    return {
      kind: "customer",
      text:
        customer.state === "suggested"
          ? "Confirm the customer. Prices and the line check follow."
          : "Find the customer. Prices and the line check follow.",
    };
  }
  if (lines.acceptable > 0) {
    return { kind: "accept", text: "Accept the matched items." };
  }
  if (lines.firstOpen) {
    return {
      kind: "pick",
      text: `Pick the item of ${lines.firstOpen.label.toLowerCase()}.`,
      lineId: lines.firstOpen.id,
    };
  }
  const held = heldStatusOf(detail.rules);
  if (facts.firstBlockedCheck) {
    return { kind: "check", text: `${facts.firstBlockedCheck}.`.replace(/\.\.$/, ".") };
  }
  if (held) {
    return { kind: "check", text: `${held.label}. See the rules above the lines.` };
  }
  if (!detail.work?.assignee) {
    return { kind: "take", text: "Take the case so colleagues see you work on it." };
  }
  if (!facts.replyReady) {
    return { kind: "reply", text: "Prepare the reply." };
  }
  return { kind: "copy", text: "Copy the reply and send it from Outlook." };
}

/**
 * Why there is no reply draft yet and when one comes, printed beside "Prepare reply"
 * (DK manager review SA-07, 12 Oct 2026): the customer first, then the first open line,
 * else the shadow-mode sentence the reply panel also uses. Null while a draft exists.
 */
export function replyWaitingText(
  customerState: StripCustomer["state"],
  firstOpen: StripLines["firstOpen"],
): string {
  if (customerState !== "certain") {
    return "Reply after you confirm the customer";
  }
  if (firstOpen) {
    return `Reply after you pick ${firstOpen.label.toLowerCase()}`;
  }
  return DRAFT_ON_REQUEST_TEXT;
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Typography as="span" variant="micro" className="text-fg-subtle font-bold">
        {label}
      </Typography>
      <div className="flex min-w-0 flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

export interface CaseSummaryStripProps {
  detail: QuoteCaseDetail;
  lines: StripLines;
  /** Selects a line and opens its pick dialog. */
  onPickLine?: (lineId: string) => void;
  /** Opens the next case of the queue; absent when there is none. */
  onNext?: (() => void) | null;
  /** Narrow layout for the Outlook pane: one column, no scrolling links. */
  compact?: boolean;
  className?: string;
}

export function CaseSummaryStrip({
  detail,
  lines,
  onPickLine,
  onNext,
  compact = false,
  className,
}: CaseSummaryStripProps) {
  const toast = useToastManager();
  const confirm = useCaseCustomer(detail.id);
  const accept = useAcceptMatched(detail.id);
  const assign = useCaseAssign(detail.id);
  const answer = useAnswerDraft(detail.id);
  const prepare = useCreateAnswerDraft(detail.id);
  // Read only from the cache: the strip never asks for the quotation preview itself.
  const foDraft = useFoDraft(detail.id, false);
  const [searching, setSearching] = useState(false);
  const [confirmedName, setConfirmedName] = useState<string | null>(null);

  const customer = stripCustomerOf(detail);
  const draft = answer.data ?? null;
  const replyStale = isDraftStale(draft);
  const replyReady =
    !replyStale && (Boolean(draft?.body?.trim()) || (!draft && detail.replyDraftReady === true));
  const prepareError = prepare.error ? answerDraftFailureText(prepare.error) : null;
  const firstBlockedCheck =
    foDraft.data?.checks?.find((check) => check.status === "blocked")?.label ?? null;
  const step = nextStepOf(detail, lines, customer, { replyReady, firstBlockedCheck });
  const reasons = FEATURES.backForYou
    ? backForYouReasons(detail.attention, customer.state === "certain" ? customer.name : null)
    : [];
  const confirmError = workFailureText(confirm.error, "customer");
  const acceptError = workFailureText(accept.error, "line");
  const leadText =
    lines.leadTimeUnknown == null ? null : leadTimeUnknownText(lines.leadTimeUnknown);
  const open = (detail.workStatus ?? "open") === "open";
  const canAccept = FEATURES.acceptMatched && open && lines.acceptable > 0;

  const confirmAccount = (option: SuggestedCustomer) => {
    logger.debug("[case] strip: confirm customer", { case: detail.id, account: option.account });
    confirm.mutate(
      { account: option.account },
      { onSuccess: () => setConfirmedName(suggestedCustomerText(option)) },
    );
  };
  const acceptMatched = () => {
    if (!canAccept || accept.isPending) {
      return;
    }
    logger.debug("[case] strip: accept matched", { case: detail.id, lines: lines.acceptable });
    accept.mutate(null, {
      onSuccess: (result) =>
        toast.add({
          title: "Matched items accepted",
          description: acceptedText(result?.accepted?.length ?? 0),
          type: "success",
        }),
    });
  };
  const takeCase = () => {
    logger.debug("[case] strip: take", { case: detail.id });
    assign.mutate({ action: "take" });
  };
  const prepareReply = () => {
    logger.debug("[case] strip: prepare reply", { case: detail.id, stale: replyStale });
    prepare.mutate(replyStale);
    if (!compact) {
      document
        .getElementById("reply-draft")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useShortcutHandler("accept", canAccept ? acceptMatched : null);
  const firstOption = customer.state === "suggested" ? customer.options[0] : undefined;
  // The `c` key confirms only a suggestion with evidence; a guess takes a deliberate click.
  useShortcutHandler(
    "confirmCustomer",
    firstOption && open && confirmsInOneClick(firstOption)
      ? () => confirmAccount(firstOption)
      : null,
  );

  const stepAction = (() => {
    switch (step.kind) {
      case "accept":
        return canAccept ? acceptMatched : null;
      case "pick":
        return onPickLine ? () => onPickLine(step.lineId) : null;
      case "take":
        return takeCase;
      case "reply":
        return prepareReply;
      default:
        return null;
    }
  })();
  const stepLabel =
    step.kind === "accept"
      ? "Accept"
      : step.kind === "pick"
        ? "Pick item"
        : step.kind === "take"
          ? "Take"
          : step.kind === "reply"
            ? "Prepare reply"
            : null;

  return (
    <Card
      variant="outline"
      padding="none"
      className={cn("overflow-hidden", className)}
      aria-label="What is left to decide"
      data-summary-strip
    >
      <div
        className={cn(
          "flex flex-col gap-3 px-4 py-3",
          !compact && "md:grid md:grid-cols-[1.3fr_1fr_1.3fr_auto] md:items-start md:gap-4",
        )}
      >
        <Section label="Customer">
          {customer.state === "certain" ? (
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-fg inline-flex min-w-0 items-center gap-1.5 text-sm font-semibold">
                <Icon icon={Check} size="xs" className="text-success-fg shrink-0" />
                <span className="min-w-0 break-words">
                  {customer.name || customerConfirmedLabel(detail)}
                </span>
              </span>
              {FEATURES.ownerSuggestion && detail.usualOwner && (
                <span
                  className="text-fg-subtle text-xs break-words"
                  title={usualOwnerTip(detail.usualOwner) ?? undefined}
                  data-usual-owner
                >
                  {usualOwnerText(detail.usualOwner, personName)}
                </span>
              )}
              {FEATURES.ownerSuggestion &&
                salesResponsibleNote(detail.usualOwner, detail.customerHeader, personName) && (
                  <span className="text-fg-subtle text-xs break-words">
                    {salesResponsibleNote(detail.usualOwner, detail.customerHeader, personName)}
                  </span>
                )}
            </span>
          ) : customer.state === "suggested" ? (
            <>
              {customer.options.map((option, index) => (
                <Button
                  key={option.account}
                  size="sm"
                  tone={index === 0 ? "accent" : "neutral"}
                  variant={index === 0 ? "solid" : "outline"}
                  loading={confirm.isPending && confirm.variables?.account === option.account}
                  disabled={confirm.isPending || !open}
                  startIcon={index === 0 ? <Icon icon={Check} size="xs" /> : undefined}
                  className="h-auto max-w-full text-left whitespace-normal"
                  onClick={() => confirmAccount(option)}
                >
                  {index === 0 ? "Confirm " : ""}
                  {suggestedCustomerText(option)}
                </Button>
              ))}
            </>
          ) : (
            <Typography as="span" variant="body-sm" className="text-warning-fg font-semibold">
              Customer not identified
            </Typography>
          )}
          {customer.state !== "certain" && (
            <Button
              size="sm"
              variant="ghost"
              tone="neutral"
              disabled={confirm.isPending || !open}
              startIcon={<Icon icon={Search} size="xs" />}
              onClick={() => {
                confirm.reset();
                setSearching(true);
              }}
            >
              Search
            </Button>
          )}
        </Section>

        <Section label="Lines">
          <span className="text-fg text-sm">{stripLinesText(lines)}</span>
          {canAccept && (
            <Button
              size="sm"
              tone="accent"
              variant={step.kind === "accept" ? "solid" : "outline"}
              loading={accept.isPending}
              startIcon={<Icon icon={ListChecks} size="xs" />}
              onClick={acceptMatched}
            >
              {acceptMatchedLabel(lines.acceptable)}
            </Button>
          )}
          {leadText && (
            <Badge tone="warning" variant="outline" size="sm" className="h-auto whitespace-normal">
              {leadText}
            </Badge>
          )}
        </Section>

        <Section label="Next step">
          <span className="text-fg text-sm break-words">{step.text}</span>
          {stepAction && stepLabel && step.kind !== "accept" && (
            <Button size="sm" variant="outline" tone="accent" onClick={stepAction}>
              {stepLabel}
            </Button>
          )}
        </Section>

        <Section label="Reply">
          {replyStale && (
            <Badge tone="warning" variant="outline" size="sm" className="h-auto whitespace-normal">
              {MADE_BEFORE_CHANGES}
            </Badge>
          )}
          {replyReady ? (
            <Badge tone="success" variant="subtle" size="sm" className="h-auto whitespace-normal">
              {draft?.created_by === PREPARED_BY ? PREPARED_ON_ARRIVAL : "Reply ready"}
            </Badge>
          ) : (
            <Button
              size="sm"
              variant="outline"
              tone="neutral"
              loading={prepare.isPending}
              disabled={!open}
              startIcon={<Icon icon={Sparkles} size="xs" />}
              onClick={prepareReply}
            >
              {replyStale ? "Prepare again" : "Prepare reply"}
            </Button>
          )}
          {!replyReady && !replyStale && open && (
            <span className="text-fg-muted text-xs break-words" data-reply-waiting>
              {replyWaitingText(customer.state, lines.firstOpen)}
            </span>
          )}
        </Section>
      </div>

      {(confirmedName || reasons.length > 0 || confirmError || acceptError || prepareError) && (
        <div className="border-border-subtle bg-bg-inset flex flex-col gap-1.5 border-t px-4 py-2">
          {confirmedName && (
            <span className="flex flex-wrap items-center gap-2" role="status">
              <span className="text-fg text-sm break-words">
                {customerConfirmedText(confirmedName)}
              </span>
              {onNext && (
                <Button
                  size="sm"
                  tone="accent"
                  endIcon={<Icon icon={ArrowRight} size="xs" />}
                  onClick={onNext}
                >
                  Next case
                </Button>
              )}
            </span>
          )}
          {reasons.map((reason) => (
            <span key={reason} className="text-fg text-sm font-semibold break-words">
              Back for you: {reason}
            </span>
          ))}
          {(confirmError || acceptError) && (
            <span className="text-danger-fg text-xs break-words" role="alert">
              {confirmError ?? acceptError}
            </span>
          )}
          {prepareError && (
            <span className="text-danger-fg text-xs break-words" role="alert" data-prepare-error>
              Could not prepare the reply: {prepareError}
            </span>
          )}
        </div>
      )}

      {searching && (
        <PickCustomerDialog
          open
          onOpenChange={setSearching}
          caseId={detail.id}
          busy={confirm.isPending}
          error={confirmError}
          initialSearch={customerSearchStart(detail)}
          caseAccounts={caseAccountsOf(detail)}
          onPick={(account) =>
            confirm.mutate(
              { account },
              {
                onSuccess: () => {
                  setSearching(false);
                  setConfirmedName(`Account ${account}`);
                },
              },
            )
          }
        />
      )}
    </Card>
  );
}
